import {
  ENTITY_TYPE_INSURANCE,
  ENTITY_TYPE_TAKAFUL,
  GENDER_MALE,
  I18N_LANGUAGE_EN,
  INSURANCE,
  ONE_MEDICAL,
  TAKAFUL,
  UNCATEGORIZED_OCCUPATION,
} from '@/constants';
import { IndustryName } from '@/enums/IndustryName';
import { ProductEnums } from '@/enums/ProductEnums';
import { Relationship } from '@/enums/Relationship';
import { Occupation } from '@/types/Facility/OccupationClass';
import { isMobile } from 'react-device-detect';

export const getApplicantIcNo = (
  dataApplicant: any,
  dataState: any,
  role: string,
) => {
  const person = dataApplicant?.find((e: any) => e.relationship === role);
  if (person) {
    const isExist = dataState.applicants.filter((a: any) => a.id === person.id);
    if (isExist) {
      return person;
    }
  }
};

export const getApplicantFromId = (dataState: any, id: number) => {
  const person = dataState?.find((e: any) => e.id === id);
  return person;
};

export const applicantName = (
  relationship: string,
  gender: string,
  product: string,
): string => {
  switch (relationship) {
    case Relationship.Self:
      return `form.application.yourself.${product}`;
    case Relationship.Spouse:
      return 'form.application.spouse';
    case Relationship.Child:
      if (gender === GENDER_MALE) return 'form.application.son';
      else return 'form.application.daughter';
    default:
      return 'form.application.beneficiary';
  }
};

export const escapeHtml = (unsafeText: string) => {
  return (
    unsafeText
      .replace(/&/g, '&amp;')
      // .replace(/</g, '&lt;')
      // .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
  );
};

export const formatText = (text: string): string[] => {
  return escapeHtml(text).split('\n');
};

export const getEntityType = (routeSection: any) => {
  if (routeSection === INSURANCE) return ENTITY_TYPE_INSURANCE;
  else if (routeSection === TAKAFUL) return ENTITY_TYPE_TAKAFUL;
  else return 0;
};

export const thousandSeparator = (param: number | undefined) => {
  if (param !== undefined) {
    return parseFloat(param.toFixed(2)).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  } else return param;
};

export const handleMobileNumberChange = (value: string) => {
  if (value && value.charAt(0) === '+') {
    return value.slice(3);
  }
  return value;
};

export function formatDateString(inputDateString) {
  // Parse the input date string
  const date = new Date(inputDateString);

  // Extract the components of the date
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  const milliseconds = String(date.getMilliseconds()).padStart(3, '0');

  // Format the timezone offset
  const timezoneOffset = date.getTimezoneOffset();
  const absOffsetHours = String(
    Math.abs(Math.floor(timezoneOffset / 60)),
  ).padStart(2, '0');
  const absOffsetMinutes = String(Math.abs(timezoneOffset % 60)).padStart(
    2,
    '0',
  );
  const timezoneSign = timezoneOffset > 0 ? '-' : '+';
  const timezone = `${timezoneSign}${absOffsetHours}:${absOffsetMinutes}`;

  // Construct the formatted date string
  const formattedDateString = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}${timezone}`;

  return formattedDateString;
}

export async function hashData(data: string) {
  const encoder = new TextEncoder();
  const dataBuffer = encoder.encode(data);

  const hashBuffer = await crypto.subtle.digest('SHA-256', dataBuffer);

  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');

  return hashHex;
}

export const getProductBanner = (
  product: string,
  routeSection: string,
  language: string,
): ProductEnums => {
  const isInsurance = routeSection === INSURANCE;
  const isEnglish = language === I18N_LANGUAGE_EN;

  if (product === ONE_MEDICAL) {
    if (isInsurance) {
      return isMobile
        ? isEnglish
          ? ProductEnums.OneMedicalInsuranceEnMobile
          : ProductEnums.OneMedicalInsuranceMyMobile
        : isEnglish
        ? ProductEnums.OneMedicalInsuranceEn
        : ProductEnums.OneMedicalInsuranceMy;
    }
    if (!isInsurance) {
      return isMobile
        ? isEnglish
          ? ProductEnums.OneMedicalTakafulEnMobile
          : ProductEnums.OneMedicalTakafulMyMobile
        : isEnglish
        ? ProductEnums.OneMedicalTakafulEn
        : ProductEnums.OneMedicalTakafulMy;
    }
  } else {
    return ProductEnums.EasyMedicEn; // Default for non-ONE_MEDICAL products
  }
};

export const filterOccupationDropdownValues = (data: Occupation[]) => {
  // transformData
  const map = new Map<string, Occupation[]>();
  let categoryCode: IndustryName | string;

  data?.forEach((item) => {
    // getCategoryFromCode
    const match = item.code.match(/^[A-Z]+/);

    if (!match) {
      categoryCode = IndustryName.UNL;
    }
    if (match && match[0]) {
      categoryCode =
        IndustryName[match[0] as keyof typeof IndustryName] ||
        UNCATEGORIZED_OCCUPATION;
    }
    const category = categoryCode;
    if (!map.has(category)) {
      map.set(category, []);
    }
    map.get(category)!.push(item);
  });

  return Array.from(map, ([category, occupations]) => ({
    category,
    occupations: occupations.sort((a, b) =>
      a.description.localeCompare(b.description),
    ),
  }));
};

export const convertToBlob = (base64: string) => {
  try {
    // Remove data URL prefix if present
    const base64Data = base64.split(',')[1] || base64;

    // Decode the Base64 string
    const byteCharacters = window.atob(base64Data);

    // Convert to byte array
    const byteArray = new Uint8Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteArray[i] = byteCharacters.charCodeAt(i);
    }

    // Create a Blob and generate a URL
    const blob = new Blob([byteArray], { type: 'application/pdf' });
    const blobUrl = URL.createObjectURL(blob);
    return blobUrl;
  } catch {}
};
